/**
 * RemindersDashboard.js
 * Tableau de bord de suivi des rappels de souscriptions en attente
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

import { Bell, Clock, AlertTriangle, TrendingUp, RefreshCw, Play, Mail, BarChart3, Loader2, XCircle, Timer, Target } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RemindersDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [_archiving, setArchiving] = useState(false);
  
  // État pour les archives
  const [showArchives, _setShowArchives] = useState(false);
  const [_archives, setArchives] = useState([]);
  const [_archivesLoading, setArchivesLoading] = useState(false);
  const [archivesPage, _setArchivesPage] = useState(1);
  const [_archivesTotal, setArchivesTotal] = useState(0);
  const [archivesSearch, _setArchivesSearch] = useState('');
  const [_restoringId, setRestoringId] = useState(null);

  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/souscriptions/reminders/stats`, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        setStats(response.data.stats);
      } else {
        toast.error(response.data.error || 'Erreur lors du chargement des statistiques');
      }
    } catch {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Charger les archives
  const fetchArchives = useCallback(async () => {
    setArchivesLoading(true);
    try {
      const params = new URLSearchParams({
        page: archivesPage.toString(),
        limit: '10'
      });
      if (archivesSearch) params.append('search', archivesSearch);
      
      const response = await axios.get(`${API}/souscriptions/archives/list?${params}`, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        setArchives(response.data.archives || []);
        setArchivesTotal(response.data.total || 0);
      }
    } catch {
    } finally {
      setArchivesLoading(false);
    }
  }, [getAuthHeader, archivesPage, archivesSearch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (showArchives) {
      fetchArchives();
    }
  }, [showArchives, fetchArchives]);

  const handleTriggerReminders = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir déclencher manuellement les rappels ?')) {
      return;
    }
    
    setTriggering(true);
    try {
      const response = await axios.post(`${API}/souscriptions/reminders/trigger`, {}, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        toast.success('Rappels déclenchés avec succès');
        fetchStats();
      } else {
        toast.error(response.data.error || 'Erreur lors du déclenchement');
      }
    } catch {
      toast.error('Erreur lors du déclenchement des rappels');
    } finally {
      setTriggering(false);
    }
  };

  const _handleTriggerArchive = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver toutes les souscriptions expirées ?')) {
      return;
    }
    
    setArchiving(true);
    try {
      const response = await axios.post(`${API}/souscriptions/archives/trigger`, {}, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        const result = response.data.result;
        toast.success(`${result.archived} souscription(s) archivée(s)`);
        fetchStats();
        if (showArchives) fetchArchives();
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'archivage');
      }
    } catch {
      toast.error('Erreur lors de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };

  const _handleRestoreArchive = async (archiveId) => {
    if (!window.confirm('Restaurer cette souscription ? Elle aura 30 jours supplémentaires pour être finalisée.')) {
      return;
    }
    
    setRestoringId(archiveId);
    try {
      const response = await axios.post(`${API}/souscriptions/archives/${archiveId}/restore`, {}, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        toast.success('Souscription restaurée avec succès');
        fetchArchives();
        fetchStats();
      } else {
        toast.error(response.data.error || 'Erreur lors de la restauration');
      }
    } catch {
      toast.error('Erreur lors de la restauration');
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        <span className="ml-3 text-gray-600">Chargement des statistiques...</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-amber-500" />
        <p>Impossible de charger les statistiques</p>
        <Button onClick={fetchStats} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-6 w-6 text-amber-500" />
            Tableau de bord des Rappels
          </h2>
          <p className="text-gray-500 mt-1">
            Suivi des rappels automatiques pour les souscriptions en attente
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchStats} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          {user?.role === 'super_admin' && (
            <Button 
              onClick={handleTriggerReminders} 
              disabled={triggering}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {triggering ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Déclencher manuellement
            </Button>
          )}
        </div>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total en attente */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total en attente</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_en_attente}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rappels envoyés */}
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rappels envoyés</p>
                <p className="text-3xl font-bold text-gray-900">{stats.rappels?.total_rappels || 0}</p>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <Badge className="bg-amber-600 text-white font-semibold">
                    21j: {stats.rappels?.rappel_21j_envoyes || 0}
                  </Badge>
                  <Badge className="bg-red-600 text-white font-semibold">
                    28j: {stats.rappels?.rappel_28j_envoyes || 0}
                  </Badge>
                </div>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <Mail className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Taux de conversion */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Conversions après rappel</p>
                <p className="text-3xl font-bold text-gray-900">
                  {(stats.conversions?.finalisees_apres_rappel_21j || 0) + (stats.conversions?.finalisees_apres_rappel_28j || 0)}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <Badge className="bg-emerald-600 text-white font-semibold">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    {stats.conversions?.taux_conversion_21j || 0}%
                  </Badge>
                </div>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <Target className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expirées */}
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expirées (&gt;30j)</p>
                <p className="text-3xl font-bold text-gray-900">{stats.expirees || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Répartition par tranche */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            Répartition par ancienneté
          </CardTitle>
          <CardDescription>
            Nombre de souscriptions en attente par tranche de jours
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.par_tranche && Object.entries(stats.par_tranche).map(([key, data]) => {
              const percentage = stats.total_en_attente > 0 
                ? Math.round((data.count / stats.total_en_attente) * 100) 
                : 0;
              const isUrgent = key.includes('22') || key.includes('29');
              
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={`font-medium ${isUrgent ? 'text-red-600' : 'text-gray-700'}`}>
                      {isUrgent && <AlertTriangle className="h-4 w-4 inline mr-1" />}
                      {data.label}
                    </span>
                    <span className="text-gray-500">
                      {data.count} ({percentage}%)
                    </span>
                  </div>
                  <Progress 
                    value={percentage} 
                    className={`h-2 ${isUrgent ? 'bg-red-100' : 'bg-gray-100'}`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Rappels récents */}
      {stats.rappels_recents && stats.rappels_recents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5 text-amber-500" />
              Rappels récents
            </CardTitle>
            <CardDescription>
              Derniers rappels envoyés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.rappels_recents.map((reminder, index) => (
                <div 
                  key={reminder.id || index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {reminder.raison_sociale || 'Entreprise non renseignée'}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      {reminder.reminder_21j_sent_at && (
                        <span>
                          Rappel 21j: {new Date(reminder.reminder_21j_sent_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      {reminder.reminder_28j_sent_at && (
                        <span className="text-red-600">
                          Rappel 28j: {new Date(reminder.reminder_28j_sent_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge 
                    className={
                      reminder.statut === 'validee' || reminder.statut === 'active'
                        ? 'bg-emerald-600 text-white font-semibold'
                        : 'bg-amber-600 text-white font-semibold'
                    }
                  >
                    {reminder.statut === 'validee' || reminder.statut === 'active' 
                      ? 'Finalisée' 
                      : 'En attente'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info sur le scheduler */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Bell className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">Rappels automatiques</h4>
              <p className="text-sm text-blue-700 mt-1">
                Les rappels sont envoyés automatiquement chaque jour à <strong>9h00</strong> :
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• <strong>Rappel 1 (21 jours)</strong> : Premier rappel, 9 jours avant expiration</li>
                <li>• <strong>Rappel 2 (28 jours)</strong> : Rappel URGENT, 2 jours avant expiration</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
